import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, X, FileText, CheckCircle, Clock, AlertCircle, Sparkles, Zap, Shield, BookOpen, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import './Numerisation.css';

interface FileItem {
  id: number;
  name: string;
  size: number;
  status: 'pending' | 'ocr' | 'anonymisation' | 'resume' | 'done' | 'error';
  progress: number;
}

const pipelineSteps = ['Uploadé', 'OCR', 'Anonymisation', 'Résumé', 'Prêt'];

const getStepIndex = (status: string): number => {
  switch (status) {
    case 'pending': return 0;
    case 'ocr': return 1;
    case 'anonymisation': return 2;
    case 'resume': return 3;
    case 'done': return 4;
    default: return 0;
  }
};

const getStepLabel = (status: string): string => {
  switch (status) {
    case 'pending': return 'Uploadé';
    case 'ocr': return 'OCR';
    case 'anonymisation': return 'Anonymisation';
    case 'resume': return 'Résumé';
    case 'done': return 'Prêt';
    default: return 'Uploadé';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending': return 'En attente';
    case 'ocr': return 'OCR en cours';
    case 'anonymisation': return 'Anonymisation';
    case 'resume': return 'Résumé IA';
    case 'done': return 'Terminé';
    case 'error': return 'Erreur';
    default: return status;
  }
};

const Numerisation: React.FC = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const intervalRef = useRef<number | null>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const addFiles = (newFiles: File[]) => {
    const newItems: FileItem[] = newFiles.map((f, i) => ({
      id: Date.now() + i,
      name: f.name,
      size: f.size,
      status: 'pending' as const,
      progress: 0,
    }));
    setFiles(prev => [...prev, ...newItems]);
  };

  const removeFile = (id: number) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const clearAll = () => {
    if (!isProcessing) setFiles([]);
  };

  const clearCompleted = () => {
    setFiles(prev => prev.filter(f => f.status !== 'done'));
  };

  const formatSize = (bytes: number): string => {
    if (bytes >= 1000000) return (bytes / 1000000).toFixed(2) + ' Mo';
    return (bytes / 1000).toFixed(0) + ' Ko';
  };

  const pendingFiles = files.filter(f => f.status === 'pending');
  const processingFiles = files.filter(f => ['ocr', 'anonymisation', 'resume'].includes(f.status));
  const doneFiles = files.filter(f => f.status === 'done');
  const errorFiles = files.filter(f => f.status === 'error');
  const totalSize = files.reduce((acc, f) => acc + f.size, 0);
  const treatedCount = doneFiles.length + errorFiles.length;
  const overallProgress = files.length > 0
    ? Math.round(files.reduce((acc, f) => acc + f.progress, 0) / files.length)
    : 0;

  // Watch for all files done → stop processing
  useEffect(() => {
    if (isProcessing && files.length > 0 && files.every(f => f.status === 'done' || f.status === 'error')) {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsProcessing(false);
    }
  }, [files, isProcessing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleLaunchClick = () => {
    if (pendingFiles.length > 0) {
      setShowModal(true);
    }
  };

  const startProcessing = useCallback(() => {
    setShowModal(false);
    setIsProcessing(true);

    const count = files.filter(f => f.status === 'pending').length;
    toast.info(`Traitement par lot lancé — ${count} fichier(s) en cours de traitement.`);

    intervalRef.current = window.setInterval(() => {
      setFiles(prev =>
        prev.map(f => {
          if (f.status === 'done' || f.status === 'error') return f;

          // Start pending files
          if (f.status === 'pending') {
            return { ...f, status: 'ocr' as const, progress: 3 };
          }

          const increment = Math.random() * 5 + 2;
          let newProgress = Math.min(f.progress + increment, 100);
          let newStatus: FileItem['status'] = f.status;

          if (newProgress >= 100) {
            newProgress = 100;
            newStatus = 'done';
          } else if (newProgress >= 75) {
            newStatus = 'resume';
          } else if (newProgress >= 45) {
            newStatus = 'anonymisation';
          } else {
            newStatus = 'ocr';
          }

          return { ...f, progress: newProgress, status: newStatus };
        })
      );
    }, 350);
  }, [files]);

  return (
    <div className="numerisation">
      {/* Drop zone */}
      <div
        className="num-drop-zone"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <div className="num-drop-icon">
          <Upload size={28} />
        </div>
        <p className="num-drop-title">Glissez vos décisions ou cliquez pour parcourir</p>
        <p className="num-drop-info">PDF, DOC, DOCX uniquement · Max 20 Mo par fichier · Jusqu'à 50 fichiers par lot</p>
        <div className="num-drop-tags">
          <span className="num-tag"><Sparkles size={12} /> OCR avancé</span>
          <span className="num-tag"><Shield size={12} /> Anonymisation IA</span>
          <span className="num-tag"><BookOpen size={12} /> Résumé automatique</span>
        </div>
      </div>

      {/* When files exist */}
      {files.length > 0 && (
        <>
          {/* Action bar */}
          <div className="num-action-bar">
            <div className="num-action-info">
              <span>À traiter <strong className="num-orange">{pendingFiles.length}</strong></span>
              <span className="num-sep">|</span>
              <span>Volume <strong>{formatSize(totalSize)}</strong></span>
            </div>
            <div className="num-action-btns">
              <button className="num-btn-outline" onClick={clearAll} disabled={isProcessing}>
                <Trash2 size={14} /> Tout retirer
              </button>
              <button
                className="num-btn-launch"
                onClick={handleLaunchClick}
                disabled={isProcessing || pendingFiles.length === 0}
              >
                <Zap size={16} /> Lancer le traitement ({pendingFiles.length})
              </button>
            </div>
          </div>

          {/* File list */}
          <div className="num-file-section">
            <div className="num-file-label">
              FICHIERS ({files.length}) — SUIVI EN DIRECT
            </div>
            {files.map(file => (
              <div key={file.id} className={`num-file-row ${file.status === 'done' ? 'num-file-row-done' : ''}`}>
                <div className="num-file-icon-wrap">
                  <FileText size={20} />
                </div>
                <div className="num-file-info">
                  <span className="num-file-name">{file.name}</span>
                  <span className="num-file-size">{formatSize(file.size)}</span>
                </div>
                <div className="num-file-bar-wrap">
                  <div className={`num-file-bar ${file.status === 'done' ? 'num-file-bar-done' : ''}`}>
                    <div className="num-file-bar-fill" style={{ width: `${file.progress}%` }} />
                  </div>
                </div>
                <span className={`num-status-badge ${
                  file.status === 'done' ? 'num-status-done' :
                  file.status === 'error' ? 'num-status-error' :
                  file.status === 'pending' ? 'num-status-pending' : 'num-status-active'
                }`}>
                  {file.status === 'done' && <CheckCircle size={12} />}
                  {file.status === 'pending' && <Clock size={12} />}
                  {file.status === 'error' && <AlertCircle size={12} />}
                  {getStatusLabel(file.status)}
                </span>
                <span className="num-file-pct">{Math.round(file.progress)}%</span>
                <button className="num-file-remove" onClick={() => removeFile(file.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          {/* Progression du lot */}
          <div className="num-batch">
            <div className="num-batch-header">
              <div>
                <h3 className="num-batch-title">Progression du lot</h3>
                <p className="num-batch-sub">{treatedCount} / {files.length} fichiers traités</p>
              </div>
              <span className={`num-batch-pct ${overallProgress === 100 ? 'num-batch-pct-done' : ''}`}>
                {overallProgress}%
              </span>
            </div>
            <div className="num-batch-bar">
              <div className="num-batch-bar-fill" style={{ width: `${overallProgress}%` }} />
            </div>
            <div className="num-batch-stats">
              <div className="num-bstat">
                <div className="num-bstat-dot num-bstat-dot-pending"><Clock size={10} /></div>
                <span className="num-bstat-label">EN ATTENTE</span>
                <span className="num-bstat-val">{pendingFiles.length}</span>
              </div>
              <div className="num-bstat">
                <div className="num-bstat-dot num-bstat-dot-progress"><Sparkles size={10} /></div>
                <span className="num-bstat-label">EN COURS</span>
                <span className="num-bstat-val">{processingFiles.length}</span>
              </div>
              <div className="num-bstat">
                <div className="num-bstat-dot num-bstat-dot-done"><CheckCircle size={10} /></div>
                <span className="num-bstat-label">TERMINÉ</span>
                <span className="num-bstat-val">{doneFiles.length}</span>
              </div>
              <div className="num-bstat">
                <div className="num-bstat-dot num-bstat-dot-error"><AlertCircle size={10} /></div>
                <span className="num-bstat-label">ERREUR</span>
                <span className="num-bstat-val">{errorFiles.length}</span>
              </div>
            </div>
          </div>

          {/* File de traitement (pipeline) */}
          <div className="num-pipeline">
            <div className="num-pipeline-header">
              <div>
                <h3 className="num-batch-title">File de traitement ({files.length})</h3>
                <p className="num-batch-sub">Suivez la progression OCR → Anonymisation → Résumé</p>
              </div>
              {doneFiles.length > 0 && (
                <button className="num-btn-outline" onClick={clearCompleted}>
                  <Trash2 size={14} /> Nettoyer les terminés
                </button>
              )}
            </div>

            {files.map(file => {
              const stepIdx = getStepIndex(file.status);
              const isDone = file.status === 'done';
              return (
                <div key={file.id} className={`num-pipe-card ${isDone ? 'num-pipe-card-done' : ''}`}>
                  <div className="num-pipe-card-top">
                    <div className="num-pipe-card-left">
                      <div className={`num-pipe-icon ${isDone ? 'num-pipe-icon-done' : ''}`}>
                        <FileText size={20} />
                      </div>
                      <div>
                        <span className="num-pipe-name">{file.name}</span>
                        <span className="num-pipe-meta">
                          {formatSize(file.size)} · {file.status === 'pending' ? 'Détection...' : 'TPI Abidjan'}
                        </span>
                      </div>
                    </div>
                    <div className="num-pipe-card-right">
                      <span className={`num-status-badge ${isDone ? 'num-status-done' : 'num-status-pending'}`}>
                        {isDone ? <CheckCircle size={12} /> : <Clock size={12} />}
                        {isDone ? 'Terminé' : 'En attente'}
                      </span>
                      <button className="num-file-remove" onClick={() => removeFile(file.id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="num-pipe-step-row">
                    <span className="num-pipe-step-label">Étape : {getStepLabel(file.status)}</span>
                    <span className="num-pipe-step-pct">{Math.round(file.progress)}%</span>
                  </div>
                  {/* Segmented pipeline bar */}
                  <div className="num-pipe-bar">
                    {pipelineSteps.map((_, i) => {
                      let fillPct = 0;
                      if (i < stepIdx) fillPct = 100;
                      else if (i === stepIdx && !isDone) {
                        const stepsTotal = pipelineSteps.length;
                        const stepSize = 100 / stepsTotal;
                        const stepStart = i * stepSize;
                        fillPct = Math.min(((file.progress - stepStart) / stepSize) * 100, 100);
                        fillPct = Math.max(fillPct, 0);
                      } else if (isDone) {
                        fillPct = 100;
                      }
                      return (
                        <div key={i} className="num-pipe-segment">
                          <div
                            className={`num-pipe-segment-fill ${isDone ? 'num-pipe-seg-done' : 'num-pipe-seg-active'}`}
                            style={{ width: `${fillPct}%` }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="num-pipe-labels">
                    {pipelineSteps.map((step, i) => (
                      <span
                        key={i}
                        className={`num-pipe-lbl ${i <= stepIdx ? (isDone ? 'num-pipe-lbl-done' : 'num-pipe-lbl-active') : ''}`}
                      >
                        {step}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Empty state */}
      {files.length === 0 && (
        <div className="num-empty">
          <div className="num-empty-header">
            <h3 className="num-batch-title">File de traitement (0)</h3>
            <p className="num-batch-sub">Suivez la progression OCR → Anonymisation → Résumé</p>
          </div>
          <div className="num-empty-body">
            <Sparkles size={32} className="num-empty-icon" />
            <p>Aucun fichier en file d'attente. Importez des décisions pour commencer.</p>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {showModal && (
        <div className="num-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="num-modal" onClick={e => e.stopPropagation()}>
            <div className="num-modal-head">
              <div className="num-modal-title-row">
                <div className="num-modal-icon">
                  <Upload size={20} />
                </div>
                <div>
                  <h3 className="num-modal-title">Confirmer le traitement par lot</h3>
                  <p className="num-modal-subtitle">Vérifiez le récapitulatif avant de lancer le traitement.</p>
                </div>
              </div>
              <button className="num-modal-close" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="num-modal-body">
              <div className="num-modal-summary">
                <div className="num-modal-row">
                  <span>Fichiers à traiter</span>
                  <strong className="num-orange">{pendingFiles.length}</strong>
                </div>
                <div className="num-modal-row">
                  <span>Volume total</span>
                  <strong>{formatSize(totalSize)}</strong>
                </div>
              </div>
              <div className="num-modal-files">
                {pendingFiles.map(f => (
                  <div key={f.id} className="num-modal-file">
                    <span>{f.name}</span>
                    <span className="num-modal-file-size">{formatSize(f.size)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="num-modal-foot">
              <button className="num-btn-cancel" onClick={() => setShowModal(false)}>
                Annuler
              </button>
              <button className="num-btn-confirm" onClick={startProcessing}>
                <Zap size={14} /> Confirmer et lancer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Numerisation;
